import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
// Direct path, not the @features/dataroom barrel: the barrel pulls react-pdf,
// which breaks jsdom in unit tests and bloats this hook's chunk.
import { dataroomApi } from '@features/dataroom/api/dataroomApi';

import { aiApi, useCancelTurnMutation, useGetChatTurnQuery, useStartTurnMutation } from '../api/aiApi';
import {
  addAssistantPlaceholder,
  addOptimisticMessage,
  clearActiveTurn,
  clearPendingQuestions,
  removeAssistantPlaceholder,
  selectActiveTurn,
  setActiveTurn,
  setError,
  setPendingQuestions,
  setSending,
  setThinkingSteps,
  updateAssistantMessage,
} from '../store/aiSlice';

// The turn runs on Celery (#256); there is no token stream, so the assistant
// row is polled until it settles. Tighter than the deck poll (5 s) because the
// thinking steps of a chat reply should feel live.
const POLL_MS = 1500;
const ACTIVE = new Set(['pending', 'running']);
const TERMINAL = new Set(['done', 'cancelled', 'failed']);
// Tools whose completion changes what the dataroom shows.
const DATAROOM_TOOLS = new Set([
  'modify_document',
  'edit_report_source',
  'create_folder',
  'move_files',
  'rename_file',
  'delete_files',
]);

/** The assistant row still in flight in a server message list, if any. */
export function findRunningTurn(rows) {
  const last = rows?.at(-1);
  return last && last.role === 'assistant' && ACTIVE.has(last.status) ? last : null;
}

/**
 * Drive one agent chat turn over the Celery + poll transport.
 *
 * `sendTurn` echoes the message at once, starts the turn and records it as the
 * active turn for this conversation; the hook then polls the turn until it
 * settles and projects the outcome onto the store. `cancel` stops it on the
 * server and settles the UI without waiting for the worker. `onSettled` gets
 * the final row of a successful turn plus the tools it ran, for the canvas.
 */
export function useChatTurn(conversationUuid, { onSettled } = {}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeTurn = useAppSelector(selectActiveTurn);
  const isMine = Boolean(conversationUuid && activeTurn?.conversationUuid === conversationUuid);
  const [startTurn] = useStartTurnMutation();
  const [cancelTurnMutation] = useCancelTurnMutation();
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const settledRef = useRef(null);

  const { data: turn } = useGetChatTurnQuery(
    { conversationUuid, messageUuid: activeTurn?.messageUuid },
    { skip: !isMine, pollingInterval: POLL_MS, skipPollingIfUnfocused: true },
  );

  // Every tick mirrors the steps the worker persisted since the last one.
  useEffect(() => {
    if (isMine && turn && ACTIVE.has(turn.status)) {
      dispatch(setThinkingSteps(turn.thinking_steps || []));
    }
  }, [turn, isMine, dispatch]);

  // Settle exactly once per turn, then fetch the real rows: the terminal
  // dispatches release isSending, which is what lets ChatPanel sync them.
  useEffect(() => {
    if (!isMine || !turn || !TERMINAL.has(turn.status) || settledRef.current === turn.uuid) return;
    settledRef.current = turn.uuid;
    const steps = turn.thinking_steps || [];
    const ranTools = new Set(steps.map((step) => step.tool));

    dispatch(clearActiveTurn());
    if (turn.status === 'done') {
      dispatch(
        updateAssistantMessage({
          content: turn.content,
          citations: turn.citations,
          toolCalls: turn.tool_calls,
          thinkingSteps: steps,
          generatedFiles: turn.generated_files,
        }),
      );
      if (turn.pending_questions?.length > 0) {
        dispatch(setPendingQuestions({ messageUuid: turn.uuid, questions: turn.pending_questions }));
      } else {
        dispatch(clearPendingQuestions());
      }
      onSettledRef.current?.({ row: turn, ranTools });
    } else {
      dispatch(removeAssistantPlaceholder());
      if (turn.status === 'failed') dispatch(setError(turn.error || t('ai.chatTurn.agentCouldNotRespond')));
    }
    if ([...ranTools].some((tool) => DATAROOM_TOOLS.has(tool))) {
      dispatch(dataroomApi.util.invalidateTags(['Folder', 'Document', 'DocumentVersion']));
    }
    dispatch(aiApi.util.invalidateTags([{ type: 'Message', id: conversationUuid }]));
  }, [turn, isMine, conversationUuid, dispatch, t]);

  // The target conversation is explicit: a message sent into a conversation
  // created a moment ago may arrive before the store has selected it.
  const sendTurn = useCallback(
    async (content, targetConversationUuid, { canvasDocumentUuid, canvasPage } = {}) => {
      dispatch(addOptimisticMessage(content)); // immediate echo; sets isSending
      dispatch(addAssistantPlaceholder());
      let started;
      try {
        started = await startTurn({
          conversationUuid: targetConversationUuid,
          content,
          canvasDocumentUuid,
          canvasPage,
        }).unwrap();
      } catch (error) {
        dispatch(removeAssistantPlaceholder());
        dispatch(setError(error?.data?.detail || t('ai.chatTurn.replyNotStarted')));
        throw error;
      }
      settledRef.current = null;
      dispatch(setActiveTurn({ conversationUuid: targetConversationUuid, messageUuid: started.message_uuid }));
      return started;
    },
    [dispatch, startTurn, t],
  );

  const cancel = useCallback(async () => {
    const pending = activeTurn;
    // Settle the UI first: stopping must feel instant even if the network is slow.
    dispatch(clearActiveTurn());
    dispatch(removeAssistantPlaceholder());
    if (!pending) {
      dispatch(setSending(false));
      return;
    }
    try {
      await cancelTurnMutation(pending).unwrap();
    } catch {
      dispatch(setError(t('ai.chatTurn.replyNotStopped')));
    }
  }, [activeTurn, dispatch, cancelTurnMutation, t]);

  return { sendTurn, cancel, isRunning: isMine };
}
